import React, {useCallback, useMemo} from 'react';
import {Select, SelectOption} from '@flixbus/honeycomb-react';
import LabelWithTooltip from '@/components/LabelWithTooltip/LabelWithTooltip';
import fieldInfoContent from '@/Pages/ProductionCost/constants/fieldInfoContent';
import {useBusPartner} from '@/Pages/PartnerCosts/hooks/useBusPartner';
import {useAppSelector} from '@/Pages/Common/hooks/useAppSelector';
import useCountryDriverConceptTypes from '@/Pages/ProductionCost/components/DataImportStepTwo/hooks/useCountryDriverConceptTypes';
import {useCountries} from '@/Pages/Countries/hooks/useCountries';
import {CountryRulesProps} from './CountryRules.types';

const CountryRules: React.FC<CountryRulesProps> = ({
  vehicleId,
  busPartnerId,
  countryRulesCountryId,
  errors,
  onUpdateVehicle,
  onUpdateBusScheduleValues
}) => {
  const busPartner = useBusPartner(busPartnerId ?? null);
  const {fetchCountryDriverConceptTypes} = useCountryDriverConceptTypes();

  const {countries} = useCountries();
  const countryDriverConceptTypes = useAppSelector(
    ({productionCost}) => productionCost.countryDriverConceptTypes
  );

  const countryOptions = useMemo(
    () =>
      (countries ?? [])
        .filter(
          country =>
            country?.currency === busPartner?.currency ||
            countryRulesCountryId === country?.id
        )
        .map(country => (
          <SelectOption key={country.id} value={country.id}>
            {country.name}
          </SelectOption>
        )),
    [busPartner?.currency, countries, countryRulesCountryId]
  );

  const handleSelectOnCountryRule = useCallback(
    async (countryRulesCountryId: string) => {
      onUpdateVehicle({
        countryRulesCountryId
      });

      let driverConcepts = countryDriverConceptTypes[countryRulesCountryId];

      if (!driverConcepts) {
        driverConcepts = await fetchCountryDriverConceptTypes(
          countryRulesCountryId
        );
      }

      // preselect first item if only one available
      const countryDriverConceptTypeId =
        driverConcepts?.length === 1 && driverConcepts[0].id
          ? driverConcepts[0].id
          : undefined;

      onUpdateBusScheduleValues({countryDriverConceptTypeId});
    },
    [
      onUpdateVehicle,
      countryDriverConceptTypes,
      onUpdateBusScheduleValues,
      fetchCountryDriverConceptTypes
    ]
  );

  const id = `country-rules-${vehicleId}`;

  return (
    <Select
      id={`country-rules-select-${id}`}
      label={
        <LabelWithTooltip
          label="Country rules*"
          htmlFor={`country-rules-select-${id}`}
          tooltipProps={{
            id: `country-rules-tooltip-${id}`,
            position: 'bottom',
            content: fieldInfoContent.countryRules,
            className: 'flex-1'
          }}
          disabled={!busPartner?.id}
        />
      }
      placeholder="Select a country"
      value={countryRulesCountryId ?? ''}
      disabled={!busPartner?.id}
      valid={!errors}
      infoError={
        errors?.optionality ?? errors?.nullable ?? errors?.notMatchingCurrency
      }
      onChange={e => handleSelectOnCountryRule(e.target.value)}
      data-testid={`line-vehicle-country-rules`}
    >
      {countryOptions}
    </Select>
  );
};

export default React.memo(CountryRules);
